import type {AxiosError} from 'axios';
import {useMutation, useQueryClient, type InfiniteData, type UseMutationOptions, type UseMutationResult} from 'react-query';
import type {NotificationsResponse} from '../../http/notifications/fetchNotifications';
import {markNotificationsRead} from '../../http/notifications/markNotificationsRead';
import type {UnreadNotificationsCountResponse} from '../../http/notifications/fetchUnreadNotificationsCount';
import {notificationsListQueryKey, unreadNotificationsCountQueryKey} from './queryKeys';

type MarkNotificationsContext = {
	previousList?: InfiniteData<NotificationsResponse>;
	previousCount?: UnreadNotificationsCountResponse;
};

export function useMarkNotificationsRead(
	options?: UseMutationOptions<void, AxiosError, number[], MarkNotificationsContext>,
): UseMutationResult<void, AxiosError, number[], MarkNotificationsContext> {
	const queryClient = useQueryClient();

	return useMutation<void, AxiosError, number[], MarkNotificationsContext>(
		(ids) => markNotificationsRead({ids}).then(() => undefined),
		{
			...options,
			onMutate: async (ids) => {
				await queryClient.cancelQueries(notificationsListQueryKey);
				await queryClient.cancelQueries(unreadNotificationsCountQueryKey);

				const previousList = queryClient.getQueryData<InfiniteData<NotificationsResponse>>(notificationsListQueryKey);
				const previousCount = queryClient.getQueryData<UnreadNotificationsCountResponse>(unreadNotificationsCountQueryKey);

				queryClient.setQueryData<InfiniteData<NotificationsResponse> | undefined>(notificationsListQueryKey, (current) => {
					if (!current) return current;

					return {
						...current,
						pages: current.pages.map((page) => ({
							...page,
							notifications: page.notifications?.map((notification) =>
								ids.includes(notification.id) ? {...notification, isRead: true} : notification,
							),
						})),
					};
				});

				queryClient.setQueryData<UnreadNotificationsCountResponse | undefined>(
					unreadNotificationsCountQueryKey,
					(current) => {
						if (!current) return current;

						return {
							...current,
							unreadCount: Math.max(0, (current.unreadCount ?? 0) - ids.length),
						};
					},
				);

				const context = {previousList, previousCount};
				await options?.onMutate?.(ids);

				return context;
			},
			onError: (error, ids, context) => {
				if (context?.previousList) {
					queryClient.setQueryData(notificationsListQueryKey, context.previousList);
				}

				if (context?.previousCount) {
					queryClient.setQueryData(unreadNotificationsCountQueryKey, context.previousCount);
				}

				options?.onError?.(error, ids, context);
			},
			onSettled: (data, error, ids, context) => {
				void queryClient.invalidateQueries(notificationsListQueryKey);
				void queryClient.invalidateQueries(unreadNotificationsCountQueryKey);
				options?.onSettled?.(data, error, ids, context);
			},
			onSuccess: (data, ids, context) => {
				options?.onSuccess?.(data, ids, context);
			},
		},
	);
}
